import { createStoreWithProducer } from '@xstate/store';
import { produce } from 'immer';

export type WindowMode = 'floating' | 'docked';

export type FloatingPosition = { x: number; y: number };

export type FloatingWindowStoreContext = {
  // Session state
  isOpen: boolean;
  /** Derived from the current page; never persisted. */
  appointmentId: string | null;

  // User preferences (persisted by the extension, never cleared)
  mode: WindowMode;
  dockedWidth: number | undefined;
  floatingHeight: number | undefined;
  /** Drag offset relative to the window's CSS anchor (right edge of the viewport); floating mode only. */
  floatingPosition: FloatingPosition | undefined;

  /**
   * Pending open/close request, for callers that don't know the current
   * page's appointmentId (e.g. embedded-recording). Set via requestOpen/
   * requestClose; null = no request pending. Consumed and cleared by
   * EhrRecordingWidget.
   */
  openRequested: boolean | null;
  /** True once persisted preferences have been applied. */
  hydrated: boolean;
};

const DEFAULT_WINDOW_MODE: WindowMode = 'floating';

const defaultContext: FloatingWindowStoreContext = {
  isOpen: false,
  appointmentId: null,
  mode: DEFAULT_WINDOW_MODE,
  dockedWidth: undefined,
  floatingHeight: undefined,
  floatingPosition: undefined,
  openRequested: null,
  hydrated: false,
};

export type FloatingWindowPreferences = {
  mode: WindowMode;
  dockedWidth?: number;
  floatingHeight?: number;
  floatingPosition?: FloatingPosition;
};

export const createFloatingWindowStore = () =>
  createStoreWithProducer(produce, defaultContext, {
    hydrate: (context, event: { prefs: FloatingWindowPreferences | null }) => {
      if (context.hydrated) {
        return;
      }
      context.hydrated = true;
      if (event.prefs) {
        context.mode = event.prefs.mode;
        context.dockedWidth = event.prefs.dockedWidth;
        context.floatingHeight = event.prefs.floatingHeight;
        context.floatingPosition = event.prefs.floatingPosition;
      }
    },
    openWindow: (context, event: { appointmentId: string | null }) => {
      context.isOpen = true;
      context.appointmentId = event.appointmentId;
    },
    closeWindow: (context) => {
      context.isOpen = false;
      context.appointmentId = null;
    },
    setOpenAppointmentId: (
      context,
      event: { appointmentId: string | null },
    ) => {
      if (!context.isOpen) {
        return;
      }
      context.appointmentId = event.appointmentId;
    },
    setMode: (context, event: { mode: WindowMode }) => {
      context.mode = event.mode;
    },
    setDockedWidth: (context, event: { width: number }) => {
      context.dockedWidth = event.width;
    },
    setFloatingHeight: (context, event: { height: number }) => {
      context.floatingHeight = event.height;
    },
    setFloatingPosition: (context, event: { position: FloatingPosition }) => {
      context.floatingPosition = event.position;
    },
    requestOpen: (context) => {
      context.openRequested = true;
    },
    requestClose: (context) => {
      context.openRequested = false;
    },
    clearRequest: (context) => {
      context.openRequested = null;
    },
    /** Test-only: restores the initial context, including the hydration guard. */
    reset: (context) => {
      Object.assign(context, defaultContext);
    },
  });

export const floatingWindowStore = createFloatingWindowStore();
